import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export enum MimeTypes {
  IMAGE_JPEG = "image/jpeg",
  IMAGE_PNG = "image/png",
  IMAGE_GIF = "image/gif",
  IMAGE_WEBP = "image/webp",
  VIDEO_MP4 = "video/mp4",
  PDF = "application/pdf",
}

const mimeTypeMap = {
  "image/jpeg": "jpeg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "application/pdf": "pdf",
};

export function getFileExtension(uri: string, mimeType: MimeTypes): string {
  const uriMatch = uri.match(/\.([^.]+)$/);
  if (uriMatch) {
    return `.${uriMatch[1].toLowerCase()}`;
  }

  return mimeTypeMap[mimeType as keyof typeof mimeTypeMap] || "";
}

export function getMimeTypeFromExtension(url: string): MimeTypes {
  const extension = url.split(".").pop();
  const reversedMimeTypeMap = Object.entries(mimeTypeMap).reduce(
    (acc, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    {} as Record<string, string>
  );
  return reversedMimeTypeMap[
    extension as keyof typeof reversedMimeTypeMap
  ] as MimeTypes;
}

export function setFileExtensionToLowercase(uri: string): string {
  const uriMatch = uri.match(/\.([^.]+)$/);
  if (!uriMatch) return uri;

  const extension = `.${uriMatch[1]}`;
  return uri.replace(extension, extension.toLowerCase());
}

export function extractUUID(str: string): string | null {
  // UUID regex pattern (8-4-4-4-12 format)
  const uuidRegex =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

  const match = str.match(uuidRegex);
  return match ? match[0] : null;
}

export function extractFirstImageUrl(markdown: string): string | null {
  const match = markdown.match(/!\[[^\]]*\]\(([^)]+)\)/);
  return match ? match[1] : null;
}

export function formatPhoneNumberToHumanReadable(value: string): string {
  // handle +1 prefix
  if (value.startsWith("+1")) value = value.slice(2);
  // 15188597340
  // handle 1 prefix
  if (value.startsWith("1")) value = value.slice(1);

  const digits = value.replace(/\D/g, "").slice(0, 10);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 10);

  let formatted = "";
  if (part1) {
    formatted += `(${part1}`;
  }
  if (digits.length >= 4) {
    formatted += `) ${part2}`;
  } else if (digits.length > 3) {
    formatted += `)`;
  }
  if (digits.length >= 7) {
    formatted += `-${part3}`;
  }
  return formatted.trim();
}

export function formatPhoneNumberToE164(value: string): string {
  if (value.length === 0) {
    return "";
  }

  // Remove all non-digits first
  const digitsOnly = value.replace(/\D/g, "");

  // If it already starts with 1 and has 11 digits, it's already formatted
  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    return "+" + digitsOnly;
  }

  // If it has 10 digits, add +1 prefix
  if (digitsOnly.length === 10) {
    return "+1" + digitsOnly;
  }

  // If it has 11 digits but doesn't start with 1, add +1 prefix
  if (digitsOnly.length === 11) {
    return "+" + digitsOnly;
  }

  // For any other case, just add +1 prefix
  return "+1" + digitsOnly;
}

export function camelCaseToTitleCase(str: string): string {
  if (!str) return "";
  return str.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
    return str.toUpperCase();
  });
}
